import { type RefObject, useState, useSyncExternalStore } from "react";
import type { GraphRect } from "./graphViewport";
import { COLUMN_PITCH, HEADER_HEIGHT, ROW_PITCH } from "./layoutGraph";

interface Props {
  scrollRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLDivElement | null>;
}

function createViewportStore({ scrollRef, canvasRef }: Props) {
  // The scroll container mounts at 0,0, so the window covers the first viewport
  // before the store can measure it.
  let snapshot: GraphRect = {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
  };
  return {
    getSnapshot: () => snapshot,
    subscribe: (notify: () => void) => {
      const scroll = scrollRef.current;
      const canvas = canvasRef.current;
      if (!scroll || !canvas) return () => {};
      let frame = 0;
      let measuredScrollTop = scroll.scrollTop;
      let measuredScrollLeft = scroll.scrollLeft;
      const measure = () => {
        frame = 0;
        measuredScrollTop = scroll.scrollTop;
        measuredScrollLeft = scroll.scrollLeft;
        const viewport = scroll.getBoundingClientRect();
        const origin = canvas.getBoundingClientRect();
        const left = viewport.left - origin.left;
        const top = viewport.top + HEADER_HEIGHT - origin.top;
        // A viewport of rows either side keeps fast scrolls from outrunning the next render.
        const overscan = Math.ceil(scroll.clientHeight / ROW_PITCH) * ROW_PITCH;
        const next = {
          left: Math.floor(left / COLUMN_PITCH) * COLUMN_PITCH - COLUMN_PITCH,
          right:
            Math.ceil((left + scroll.clientWidth) / COLUMN_PITCH) *
              COLUMN_PITCH +
            COLUMN_PITCH,
          top: Math.floor(top / ROW_PITCH) * ROW_PITCH - overscan,
          bottom:
            Math.ceil(
              (viewport.top + scroll.clientHeight - origin.top) / ROW_PITCH
            ) *
              ROW_PITCH +
            overscan,
        };
        if (
          next.left === snapshot.left &&
          next.right === snapshot.right &&
          next.top === snapshot.top &&
          next.bottom === snapshot.bottom
        )
          return;
        snapshot = next;
        notify();
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(measure);
      };
      const onScroll = () => {
        if (
          Math.abs(scroll.scrollTop - measuredScrollTop) >=
            scroll.clientHeight / 2 ||
          scroll.scrollLeft !== measuredScrollLeft
        )
          schedule();
      };
      scroll.addEventListener("scroll", onScroll, { passive: true });
      const observer = new ResizeObserver(schedule);
      observer.observe(scroll);
      observer.observe(canvas);
      measure();
      return () => {
        scroll.removeEventListener("scroll", onScroll);
        observer.disconnect();
        if (frame) cancelAnimationFrame(frame);
      };
    },
  };
}

export function useGraphViewport(props: Props): GraphRect {
  const [store] = useState(() => createViewportStore(props));
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}
